/* Copyright 2016 Google Inc. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
==============================================================================*/

import * as d3 from 'd3';

type DataPoint = {
  x: number;
  y: number; // Changed from y: number[] to y: number
};

/**
 * A single-series line chart that allows you to append new data points
 * as data becomes available.
 */
export class AppendingLineChart {
  private data: DataPoint[] = [];
  private svg;
  private xScale;
  private yScale;
  private path; // Changed from paths to path
  private lineColor: string; // Changed from lineColors to lineColor

  private minY = Number.MAX_VALUE;
  private maxY = Number.MIN_VALUE;

  constructor(container, lineColors: string[]) {
    if (lineColors.length !== 1) {
      throw new Error("AppendingLineChart now expects exactly one color for a single line.");
    }
    this.lineColor = lineColors[0];
    let node = container.node() as HTMLElement;
    let totalWidth = node.offsetWidth;
    let totalHeight = node.offsetHeight;
    let margin = {top: 2, right: 0, bottom: 2, left: 2};
    let width = totalWidth - margin.left - margin.right;
    let height = totalHeight - margin.top - margin.bottom;

    this.xScale = d3.scale.linear()
      .domain([0, 0])
      .range([0, width]);

    this.yScale = d3.scale.linear()
      .domain([0, 0])
      .range([height, 0]);

    this.svg = container.append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

    this.path = this.svg.append("path") // Only one path
      .attr("class", "line")
      .style({
        "fill": "none",
        "stroke": this.lineColor, // Use the single color
        "stroke-width": "1.5px"
      });
  }

  reset() {
    this.data = [];
    this.redraw();
    this.minY = Number.MAX_VALUE;
    this.maxY = Number.MIN_VALUE;
  }

  addDataPoint(dataPoint: number[]) { // dataPoint is now [lossTrain]
    if (dataPoint.length !== 1) {
      throw Error("addDataPoint now expects an array with a single number for the training loss.");
    }
    const yVal = dataPoint[0];
    this.minY = Math.min(this.minY, yVal);
    this.maxY = Math.max(this.maxY, yVal);

    this.data.push({x: this.data.length + 1, y: yVal}); // Store yVal directly
    this.redraw();
  }

  private redraw() {
    // Adjust the x and y domain.
    this.xScale.domain([1, this.data.length]);
    this.yScale.domain([this.minY, this.maxY]);

    // Adjust the <path> element.
    const line = d3.svg.line<DataPoint>()
      .x(d => this.xScale(d.x))
      .y(d => this.yScale(d.y)); // d.y is now a number

    this.path.datum(this.data).attr("d", line);
  }
}
